import React from "react";
import type { Dispatch, SetStateAction } from "react";
import { Box, Tooltip, CircularProgress, Divider } from "@mui/joy";
import { VisibilityIcon, DescriptionIcon } from "../../../styles/StyledMUI";
import { useAlert } from "../../../contexts/app/notificationToast";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { colorTokens, roundActionIconButtonSx, textTokens } from "../../../styles/globalStyles";
import DocumentUploadButtons from "./DocumentUploadButtons";

type FileWithPDFComponentProps = {
  file: File | string | undefined;
  setFile: Dispatch<SetStateAction<File | string | undefined>>;
  pdf: string | undefined;
};

const FileWithPDFComponent: React.FC<FileWithPDFComponentProps> = ({ file, setFile, pdf }) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { showAlert } = useAlert();

  const viewFile = () => {
    if (typeof file === "string") {
      window.open(file, "_blank", "noopener,noreferrer");
    } else if (file instanceof File) {
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Box sx={{ padding: 1, display: "flex", alignItems: "stretch", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          minWidth: 0,
        }}
      >
        {pdf ? (
          <a
            href={pdf}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            <Tooltip title="Voir le document original">
              <IconButtonCTA
                size="lg"
                ctaVariant="accent"
                aria-label="Voir le document original"
                sx={roundActionIconButtonSx}
              >
                <DescriptionIcon />
              </IconButtonCTA>
            </Tooltip>
            <DarkTypography level="body-xs" colorVariant="secondary" sx={{ fontSize: "12px" }}>
              Document original
            </DarkTypography>
            <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontSize: "11px" }}>
              (à télécharger, remplir et redéposer)
            </DarkTypography>
          </a>
        ) : null}
      </Box>

      <Divider
        orientation="vertical"
        sx={{ alignSelf: "stretch", borderColor: colorTokens.border.default }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          minWidth: 0,
        }}
      >
        {!isLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
            <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
              <DocumentUploadButtons
                onFile={setFile}
                onInvalidFile={() =>
                  showAlert("PDF ou photo requis (image illisible refusée).", "warning")
                }
                onLoadingChange={setIsLoading}
              />

              {file && (
                <Tooltip title="Voir le document ajouté">
                  <IconButtonCTA
                    size="lg"
                    onClick={viewFile}
                    ctaVariant="accent"
                    aria-label="Voir le document ajoute"
                    sx={roundActionIconButtonSx}
                  >
                    <VisibilityIcon />
                  </IconButtonCTA>
                </Tooltip>
              )}
            </Box>

            <Box>
              {file ? (
                <DarkTypography level="body-sm" sx={{ color: textTokens.color.primary }}>
                  Document renseigné
                </DarkTypography>
              ) : (
                <DarkTypography level="body-sm" colorVariant="muted">
                  Document non renseigné
                </DarkTypography>
              )}
            </Box>
          </Box>
        ) : (
          <CircularProgress />
        )}
      </Box>
    </Box>
  );
};

export default FileWithPDFComponent;
